import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../shared/tempo';
import { COUP_DE_VENT } from './coup-de-vent';
import { FULL_ENEMIES } from './full';
import { POGO } from './pogo';
import type { EnemyDefinition } from './types';

const GROWING = { hp: 1.1, speed: 1.02 };
const FIXED = { hp: 1, speed: 1 };

export const ENEMIES: readonly EnemyDefinition[] = [
  {
    id: 'random',
    name: 'Le Random',
    description: 'Il surgit de partout à la fois, faible seul mais jamais seul.',
    behaviour: 'horde',
    maxHp: 30,
    speed: 1.4,
    radius: 12,
    damage: 3,
    attackCooldownTicks: 2 * TICKS_PER_BEAT,
    aggroRadius: 80,
    vibesDrop: 1,
    scalingPerPhrase: GROWING,
  },
  {
    id: 'desagreable',
    name: 'Le Désagréable',
    description: 'Il fonce droit sur la scène et bouscule qui se met en travers.',
    behaviour: 'rusher',
    maxHp: 18,
    speed: 2.3,
    radius: 10,
    damage: 4,
    attackCooldownTicks: TICKS_PER_BEAT,
    aggroRadius: 140,
    vibesDrop: 1,
    scalingPerPhrase: GROWING,
    special: { kind: 'shove', knockback: 30 },
  },
  {
    id: 'meprisant',
    name: 'Le Méprisant',
    description: 'Il reste loin et balance des bouteilles qui ralentissent qui les reçoit.',
    behaviour: 'shooter',
    maxHp: 15,
    speed: 1.8,
    radius: 11,
    damage: 5,
    attackCooldownTicks: TICKS_PER_BAR,
    aggroRadius: 220,
    vibesDrop: 2,
    scalingPerPhrase: GROWING,
    ranged: { projectileSpeed: 7, projectileRadius: 5, rangeTicks: 30, keepDistance: 120 },
    special: { kind: 'bottle', slowFactor: 0.5, durationTicks: 2 * TICKS_PER_BAR },
  },
  {
    id: 'male-alpha',
    name: 'Le Mâle alpha',
    description:
      'Lourd et sûr de lui, il pousse ton matériel et encaisse moitié moins de face : prends-le à revers.',
    behaviour: 'heavy',
    maxHp: 140,
    speed: 1.1,
    radius: 20,
    damage: 12,
    attackCooldownTicks: 3 * TICKS_PER_BEAT,
    aggroRadius: 100,
    vibesDrop: 5,
    scalingPerPhrase: GROWING,
    special: { kind: 'frontGuard', frontDamageMul: 0.5 },
  },
  {
    id: 'collant',
    name: 'Le Collant',
    description: "Il s'accroche à toi et te ralentit jusqu'à ce qu'on le décroche.",
    behaviour: 'rusher',
    maxHp: 24,
    speed: 2.1,
    radius: 10,
    damage: 3,
    attackCooldownTicks: TICKS_PER_BEAT,
    aggroRadius: 140,
    vibesDrop: 1,
    scalingPerPhrase: GROWING,
    special: { kind: 'cling', slowFactor: 0.6, detachDamage: 10 },
  },
  {
    id: 'intolerant',
    name: "L'Intolérant",
    description: 'Il éteint les bonus de qui reste trop près : vise-le en priorité.',
    behaviour: 'horde',
    maxHp: 50,
    speed: 1,
    radius: 13,
    damage: 3,
    attackCooldownTicks: 2 * TICKS_PER_BEAT,
    aggroRadius: 90,
    vibesDrop: 2,
    scalingPerPhrase: GROWING,
    special: { kind: 'suppress', radius: 100 },
  },
  {
    id: 'arnaqueur',
    name: "L'Arnaqueur",
    description: 'Il fonce sur tes vibes au sol, les vole, puis détale si tu le poursuis.',
    behaviour: 'rusher',
    maxHp: 20,
    speed: 2.6,
    radius: 10,
    damage: 3,
    attackCooldownTicks: TICKS_PER_BEAT,
    aggroRadius: 140,
    vibesDrop: 1,
    scalingPerPhrase: GROWING,
    special: { kind: 'steal', fleeSpeedMul: 1.4 },
  },
  {
    id: 'fatigue',
    name: 'Le Fatigué',
    description: "Son bâillement se propage autour de lui, puis il s'endort sur place.",
    behaviour: 'horde',
    maxHp: 45,
    speed: 0.9,
    radius: 13,
    damage: 2,
    attackCooldownTicks: 2 * TICKS_PER_BEAT,
    aggroRadius: 90,
    vibesDrop: 2,
    scalingPerPhrase: GROWING,
    special: { kind: 'yawn', radius: 110, slowFactor: 0.6, awakeBars: 2, sleepBars: 4 },
  },
  {
    id: 'filmeur',
    name: 'Le Filmeur',
    description: 'Il filme la scène et éblouit doucement la zone de son écran.',
    behaviour: 'shooter',
    maxHp: 22,
    speed: 1.6,
    radius: 11,
    damage: 0,
    attackCooldownTicks: TICKS_PER_BAR,
    aggroRadius: 200,
    vibesDrop: 2,
    scalingPerPhrase: GROWING,
    ranged: { projectileSpeed: 6, projectileRadius: 5, rangeTicks: 50, keepDistance: 240 },
    special: { kind: 'dazzle', radius: 90 },
  },
  {
    id: 'bavard',
    name: 'Le Bavard',
    description: "Ses bulles de blabla s'affichent et masquent un bout de ton écran.",
    behaviour: 'shooter',
    maxHp: 22,
    speed: 1.6,
    radius: 11,
    damage: 0,
    attackCooldownTicks: TICKS_PER_BAR,
    aggroRadius: 200,
    vibesDrop: 2,
    scalingPerPhrase: GROWING,
    ranged: { projectileSpeed: 6, projectileRadius: 5, rangeTicks: 50, keepDistance: 240 },
    special: { kind: 'babble', everyBars: 4 },
  },
  {
    id: 'zombie',
    name: 'Le Zombie',
    description: 'Le lève-tôt du petit matin : abats-le, il se relève une fois.',
    behaviour: 'heavy',
    maxHp: 160,
    speed: 1,
    radius: 20,
    damage: 10,
    attackCooldownTicks: 3 * TICKS_PER_BEAT,
    aggroRadius: 100,
    vibesDrop: 6,
    scalingPerPhrase: GROWING,
    special: { kind: 'revive', times: 1, hpRatio: 0.5, downBars: 2 },
  },
  {
    id: 'couvre-feu',
    name: 'Le Couvre-feu',
    description: "Il veut couper le son avant l'heure.",
    behaviour: 'boss',
    maxHp: 1200,
    speed: 1,
    radius: 40,
    damage: 25,
    attackCooldownTicks: 2 * TICKS_PER_BEAT,
    aggroRadius: 300,
    vibesDrop: 40,
    scalingPerPhrase: FIXED,
  },
  {
    id: 'batterie-a-plat',
    name: 'La Batterie à plat',
    description: "Elle vide l'énergie de tout ce qui brille.",
    behaviour: 'boss',
    maxHp: 2400,
    speed: 0.9,
    radius: 48,
    damage: 35,
    attackCooldownTicks: 2 * TICKS_PER_BEAT,
    aggroRadius: 360,
    vibesDrop: 60,
    scalingPerPhrase: FIXED,
  },
  COUP_DE_VENT,
  POGO,
  ...FULL_ENEMIES,
];
